import {
  BLACK_BOOK_QUESTIONS_V2,
  transformMultiChoiceData,
  transformSingleChoiceCustomData,
  transformSingleChoiceData,
  VEHICLE_CONDITIONS_PAGE_2,
} from 'pages/tradeIn/TradeInConstant';
import { transformQuestions } from 'pages/tradeIn/TradeInUtils';
import React, { useImperativeHandle, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { renderQuestionsRecursively } from 'sharedComponents/commonHelper/questionRenderer';
import { StepperFromProps } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';
import TradeInHeader from './header/TradeInHeader';
import { getToken, postBlackBookAnswers } from 'store/CommonModuleState';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import useQueryParams from 'hooks/UseQueryParams';
import { Lead } from 'pages/leadGen/LeadGenInterface';
import { postLeadCreation } from 'store/LeadCreationState';

const Step6: React.ForwardRefRenderFunction<unknown, StepperFromProps> = ({ onNext, onBack }, ref) => {
  // Hooks
  const { t } = useTranslation();

  const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();

  const queryParams = useQueryParams();
  const uvc = queryParams.get('uvc');

  const { token } = useSelector((state: RootState) => state.commonModuleStore);
  const { vehicleOptions } = useSelector((state: RootState) => state.tradeInStore);

  const { authenticateClientData } = useSelector((state: RootState) => state.auth) || {};
  const widgetData = authenticateClientData?.data?.widgetData || {};
  const dealershipId = widgetData?.dealershipId ?? widgetData?.dealershipName;

  const hasCalledNextStep = React.useRef(false);

  // React Hook Form
  const { watch, control, trigger, getValues } = useFormContext();

  const watchAllFields = watch();

  const vehicleOptionsArray = useMemo(() => {
    return [
      {
        id: 1,
        label: t('stepperFormTradeIn.que1'),
        labelTxt: 'Select any options your vehicle has',
        options: vehicleOptions?.map((option: { name: string; _id: string }) => ({
          value: option._id,
          label: option.name,
        })),
      },
    ];
  }, [vehicleOptions]);

  // React Hook Form Trigger
  const nextStep = () => {
    if (hasCalledNextStep.current) return;
    hasCalledNextStep.current = true;

    const singleChoice = transformSingleChoiceData(transformQuestions(BLACK_BOOK_QUESTIONS_V2()), getValues(), t);

    const requestData = {
      postData: {
        vin: '',
        uvc: uvc || getValues('vehicleSearch')?.uvc,
        mileage: getValues('mileage'),
        state: getValues('province'),
        answers: singleChoice?.map(({ actualAnswer, actualAnswerTxt, ...rest }) => rest),
      },

      visitor: 'testVisitor',
      customerId: dealershipId,
      token,
    };

    dispatch(postBlackBookAnswers(requestData)).then((res) => {
      if (res?.payload == undefined) {
        dispatch(getToken(dealershipId)).then((res: any) => {
          if (res?.payload?.message?.token) {
            const updatedRequestData = {
              ...requestData,
              token: res?.payload?.message?.token,
            };

            dispatch(postBlackBookAnswers(updatedRequestData)).then((valuedRes) => {
              postLeadData(valuedRes?.payload?.message);
            });
          }
        });
      } else if (res?.payload?.message) {
        postLeadData(res?.payload?.message);
      }
    });
  };

  const postLeadData = (offerValue: { ResponseMessage: string; Value: number }) => {
    const multiChoice = transformMultiChoiceData(vehicleOptionsArray, getValues(), t);

    const singleChoice = transformSingleChoiceData(transformQuestions(BLACK_BOOK_QUESTIONS_V2()), getValues(), t);

    const customChoice = transformSingleChoiceCustomData(VEHICLE_CONDITIONS_PAGE_2(t), getValues());

    const questionData: Lead = {
      leadDetails: {
        questions: [...(multiChoice[0]?.answer?.length > 0 ? multiChoice : []), ...singleChoice, ...customChoice],
        'Offer Value': offerValue?.Value || offerValue?.ResponseMessage || 'Unavailable',
      },
    };

    console.log('questionData', questionData);

    dispatch(postLeadCreation({ data: questionData, leadTypeID: widgetData?.leadTypeId })).then((res) => {
      if (res?.payload?.success && (res?.payload?.data?.draftLeadId || res?.payload?.data?._id)) {
        onNext?.();
      }
    });
  };

  const backStep = () => {
    onBack?.(vehicleOptions?.length > 0 ? 4 : 3);
  };

  useImperativeHandle(ref, () => ({
    nextStep,
    backStep,
  }));

  return (
    <div className='dr-widget-steps d-flex flex-column h-100 step-2-1 position-relative'>
      <div className='dr-widget-steps__content'>
        <h2 className='dr-widget-steps__title'>{t('digitalRetailing.subHeading')}</h2>
        <TradeInHeader currentStep={2} />
        <div className='dr-widget-steps__form-wrapper'>
          <div className='tradein'>
            <h5 className='tradein__title mb-2'>{t('stepperFormTradeIn.vehicleConditions')}</h5>
            <p className='text-secondary mb-4'>{t('stepperFormTradeIn.vehicleConditionsDesc')}</p>
            <div className='vc-list'>
              {renderQuestionsRecursively({
                questions: transformQuestions(BLACK_BOOK_QUESTIONS_V2()),
                control,
                trigger,
                watchAllFields,
                t,
                watch,
              })}

              {renderQuestionsRecursively({
                questions: VEHICLE_CONDITIONS_PAGE_2(t),
                control,
                trigger,
                watchAllFields,
                t,
                watch,
              })}
            </div>
          </div>
        </div>
      </div>
      <div className='dealer-send-note text-secondary mt-auto'>{t('stepperFormTradeIn.note4')}</div>
    </div>
  );
};

const StepperForm6 = React.forwardRef(Step6);

export default StepperForm6;
